import { get } from 'lodash';
import type { PageMeta } from '$util/types';
import Grid from '$components/layout/Grid';
import Tile from './Tile';

const { Col } = Grid;

export const tileIcon = {
  pin: '/icons/icon_pickup-delivery_mlb8mg.svg',
  wrench: '/icons/icon_top-garages_fezqkq.svg',
  quote: '/icons/icon_quote-blue_ouroay.svg',
  shield: '/icons/icon_shield-blue_cisfzh.svg',
  manufacturer: '/icons/icon_manufacturer_s8f13g.svg',
};

const getTitle = (
  pageType: AdvantagesProps['pageType'],
  pageTypeFromPath: AdvantagesProps['pageTypeFromPath']
) => {
  switch (pageTypeFromPath) {
    case 'service':
      return 'Manufacturer-equivalent';
    case 'service/area':
      return 'Manufacturer-equivalent';
    case 'service/car':
      return 'Manufacturer-equivalent';
    case 'service/car/model':
      return 'Manufacturer-equivalent';
    case 'car':
      return `${get(pageType, '0.name')}-equivalent`;
    case 'car/model':
      return `${get(pageType, '0.name')}-equivalent`;
    case 'area':
      return `Manufacturer-equivalent`;
    case 'area/car':
      return `${get(pageType, '1.name')}-equivalent`;
    default:
      return 'Manufacturer-equivalent';
  }
};

function Tile1(): JSX.Element {
  return (
    <Tile title="Pick up and delivery" imgAlt="icon_pickup-delivery" imgSource={tileIcon.pin}>
      — anywhere
    </Tile>
  );
}

function Tile2({ pageType, pageTypeFromPath }: AdvantagesProps): JSX.Element {
  switch (pageTypeFromPath) {
    case 'area': {
      const title = `Services from ${get(pageType, '0.name')}'s`;
      const subTitle = 'top-rated garages and mechanics';
      return (
        <Tile title={title} imgAlt="icon_top-garages" imgSource={tileIcon.wrench}>
          {subTitle}
        </Tile>
      );
    }
    case 'area/car': {
      const title = `Services from ${get(pageType, '0.name')}'s`;
      const subTitle = 'top-rated garages and mechanics';
      return (
        <Tile title={title} imgAlt="icon_top-garages" imgSource={tileIcon.wrench}>
          {subTitle}
        </Tile>
      );
    }
    case 'workOperations': {
      const title = (
        <span className="-nobold">
          Services from the <b>UK&#39;s most</b>
        </span>
      );
      const subTitle = (
        <span>
          <b>garages</b> and mechanics
        </span>
      );
      return (
        <Tile
          className="-workOperationsPage"
          title={title}
          imgAlt="icon_top-garages"
          imgSource={tileIcon.wrench}
        >
          {subTitle}
        </Tile>
      );
    }
    default:
      return (
        <Tile title="Top-rated mechanics" imgAlt="icon_top-garages" imgSource={tileIcon.wrench}>
          and garages across the UK
        </Tile>
      );
  }
}

function Tile3({ pageTypeFromPath }: Omit<AdvantagesProps, 'pageType'>): JSX.Element {
  switch (pageTypeFromPath) {
    case 'area': {
      const title = `In-house experts personally`;
      const subTitle = 'approve all work';
      return (
        <Tile title={title} imgAlt="icon_quote-blue" imgSource={tileIcon.quote}>
          {subTitle}
        </Tile>
      );
    }
    case 'area/car': {
      const title = `In-house experts personally`;
      const subTitle = 'approve all work';
      return (
        <Tile title={title} imgAlt="icon_quote-blue" imgSource={tileIcon.quote}>
          {subTitle}
        </Tile>
      );
    }
    case 'workOperations': {
      const title = (
        <span className="-nobold">
          Upfront, fixed quote <b>up to</b>
        </span>
      );
      const subTitle = <b>30% cheaper than average prices</b>;
      return (
        <Tile
          className="-workOperationsPage"
          title={title}
          imgAlt="icon_quote-blue"
          imgSource={tileIcon.quote}
        >
          {subTitle}
        </Tile>
      );
    }
    default:
      return (
        <Tile
          title="Upfront, fixed quotes"
          imgAlt="icon_quote-blue"
          imgSource={tileIcon.quote}
          className="cheaper-tile"
        >
          up to <span>30% cheaper</span> than average
        </Tile>
      );
  }
}

function Tile4({ pageType, pageTypeFromPath }: AdvantagesProps): JSX.Element {
  switch (pageTypeFromPath) {
    case 'workOperations': {
      const title = (
        <span className="-nobold">
          In-house experts <b>personally</b>
        </span>
      );
      const subTitle = <b>approve all work</b>;
      return (
        <Tile
          className="-workOperationsPage"
          title={title}
          imgAlt="icon_shield-blue"
          imgSource={tileIcon.shield}
        >
          {subTitle}
        </Tile>
      );
    }
    default:
      return (
        <Tile
          title={getTitle(pageType, pageTypeFromPath)}
          imgAlt="icon_manufacturer"
          imgSource={tileIcon.manufacturer}
        >
          parts only
        </Tile>
      );
  }
}

interface AdvantagesProps {
  readonly pageType?: PageMeta[];
  readonly pageTypeFromPath?: string;
}

function Advantages({ pageType, pageTypeFromPath }: AdvantagesProps): JSX.Element {
  return (
    <>
      <Col md={3} xs={12} className="Advantages">
        <Tile1 />
      </Col>
      <Col md={3} xs={12} className="Advantages">
        <Tile2 pageType={pageType} pageTypeFromPath={pageTypeFromPath} />
      </Col>
      <Col md={3} xs={12} className="Advantages">
        <Tile3 pageTypeFromPath={pageTypeFromPath} />
      </Col>
      <Col md={3} xs={12} className="Advantages">
        <Tile4 pageType={pageType} pageTypeFromPath={pageTypeFromPath} />
      </Col>
    </>
  );
}

Advantages.displayName = 'Advantages';
export default Advantages;
